/*
 * CAENE Routine Widget — bundle-läge (2026-07-21).
 * Custom element. Hydrates Liquid-rendered shell with state-bound rows.
 *
 * Trion säljs som Recharge-bundle-produkten 'bygg-ditt-eget-3-pack':
 *   one-time = bundle-variantens pris (−20% mot 3×fullpris)
 *   sub      = bundle-variantens selling plan-pris (−30% mot 3×fullpris)
 * Ett kort-globalt läge (toggle längst ner, default one-time) — inga
 * per-produkt-checkboxar; raderna visar statiska checkar + tier-pris.
 *
 * ATC-flöde (reverse-engineered från Recharge storefront-bundling-widget,
 * spike 2026-07-21):
 *   1. Bygg 132-byte selection-blob (big-endian):
 *      [u32 0][u64 bundleVariant][u32 count] + per item:
 *      [u64 collectionId][u64 productId][u64 variantId][u64 qty][u32 0]
 *      + [u32 unixTs][u32 0]
 *   2. POST {bundle: base64(blob)} → /tools/recurring/bundling-storefront-manager/api/v1/bundles
 *      (kräver Accept: application/json + X-Recharge-App-header, annars HTML-svar)
 *      → {code, id, message} — id:t är _rb_id.
 *   3. POST /cart/add.js med bundle-varianten + properties {Contents, _rb_id}
 *      (+ selling_plan i sub-läge).
 */

// Recharge-bundelns "selectable products"-collection (samma för alla trios).
var CAENE_RW_BUNDLE_COLLECTION_ID = '686214644092';

class CaeneRoutineWidget extends HTMLElement {
  connectedCallback() {
    try {
      this.products = JSON.parse(this.querySelector('script[data-products-json]').textContent);
      this.bundle = JSON.parse(this.querySelector('script[data-bundle-json]').textContent);
    } catch (e) {
      console.error('[caene-rw] Failed to parse JSON payloads', e);
      return;
    }

    this.imagesMount = this.querySelector('[data-images-mount]');
    this.cardsMount = this.querySelector('[data-cards-mount]');
    this.totalPriceEl = this.querySelector('[data-total-price]');
    this.totalStrikeEl = this.querySelector('[data-total-strike]');
    this.totalSavingsEl = this.querySelector('[data-total-savings]');
    this.cta = this.querySelector('[data-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.errorEl = this.querySelector('[data-error]');
    this.subSwitch = this.querySelector('[data-sub-switch]');
    this.subPctEl = this.querySelector('[data-sub-pct]');
    this.freqNote = this.querySelector('[data-freq-note]');

    if (!this.cardsMount || !this.imagesMount || !this.totalPriceEl || !this.cta || !this.ctaLabel || !this.errorEl || !this.subSwitch) {
      console.error('[caene-rw] Missing required DOM targets — widget aborted');
      return;
    }

    this.defaultCtaText = this.ctaLabel.textContent;
    this.subAvailable = this.bundle.sellingPlanId !== null && this.bundle.subPrice !== null;
    this.mode = 'onetime';
    this.state = this.products.map(p => ({ ...p }));

    // "Prenumerera & spara X%" — mot summan av fullpriser (3×299)
    if (this.subPctEl && this.subAvailable) {
      this.subPctEl.textContent = String(Math.round((1 - this.bundle.subPrice / this.fullSum()) * 100));
    }
    if (!this.subAvailable) {
      const footer = this.querySelector('.caene-rw__footer');
      if (footer) footer.hidden = true;
    }

    this.renderImages();
    this.renderCards();
    this.updateTotals();

    this.cta.addEventListener('click', () => this.handleCtaClick());
    this.subSwitch.addEventListener('change', () => this.setMode(this.subSwitch.checked ? 'sub' : 'onetime'));

    this._onVariantChange = (e) => this.handleVariantChange(e);
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    if (this._onVariantChange) {
      document.removeEventListener('variant:change', this._onVariantChange);
    }
  }

  fullSum() {
    return this.state.reduce((sum, s) => sum + s.fullPrice, 0);
  }

  bundleCurrent() {
    return this.mode === 'sub' && this.subAvailable ? this.bundle.subPrice : this.bundle.price;
  }

  /* Per-rad tier-pris: fullpris skalat med bundle-rabatten (jämna 299:or → 239/209) */
  itemTierPrice(s) {
    return Math.round(s.fullPrice * this.bundleCurrent() / this.fullSum());
  }

  renderImages() {
    const parts = [];
    this.state.forEach((s, i) => {
      parts.push(`
        <div class="caene-rw__image-card" data-image data-idx="${i}" data-checked="true" data-available="${s.available}">
          <img src="${s.imageUrl}" alt="${this.escape(s.title)}" loading="lazy">
        </div>
      `);
      if (i < this.state.length - 1) {
        parts.push('<span class="caene-rw__plus" aria-hidden="true">+</span>');
      }
    });
    this.imagesMount.innerHTML = parts.join('');
  }

  renderCards() {
    this.cardsMount.innerHTML = this.state.map((s, i) => this.itemHtml(s, i)).join('');
  }

  itemHtml(s, idx) {
    return `
      <div class="caene-rw__item" data-item data-idx="${idx}" data-checked="true" data-available="${s.available}">
        <div class="caene-rw__item-row caene-rw__item-row--static">
          <span class="caene-rw__check" aria-hidden="true"></span>
          <span class="caene-rw__item-title">${this.escape(s.title)}</span>
          <span class="caene-rw__item-prices">
            <span class="caene-rw__item-price--strike">${this.formatPrice(s.fullPrice)}</span>
            <span class="caene-rw__item-price" data-item-price>${this.formatPrice(this.itemTierPrice(s))}</span>
          </span>
        </div>
      </div>
    `;
  }

  setMode(mode) {
    if (mode === 'sub' && !this.subAvailable) return;
    this.mode = mode;
    this.cardsMount.querySelectorAll('[data-item]').forEach(item => {
      const idx = parseInt(item.dataset.idx, 10);
      const priceEl = item.querySelector('[data-item-price]');
      if (priceEl) priceEl.textContent = this.formatPrice(this.itemTierPrice(this.state[idx]));
    });
    if (this.freqNote) this.freqNote.hidden = this.mode !== 'sub';
    this.updateTotals();
  }

  updateTotals() {
    const total = this.bundleCurrent();
    const full = this.fullSum();

    this.totalPriceEl.textContent = this.formatPrice(total);

    if (total < full) {
      this.totalStrikeEl.textContent = this.formatPrice(full);
      this.totalStrikeEl.hidden = false;
      if (this.totalSavingsEl) {
        this.totalSavingsEl.textContent = `SPARA ${Math.round((full - total) / 100)} KR`;
        this.totalSavingsEl.hidden = false;
      }
    } else {
      this.totalStrikeEl.hidden = true;
      if (this.totalSavingsEl) this.totalSavingsEl.hidden = true;
    }
  }

  /* Recharge selection-blob (se filhuvud för layout) */
  buildBundleBlob() {
    const items = this.state;
    const buf = new ArrayBuffer(16 + items.length * 36 + 8);
    const dv = new DataView(buf);
    let o = 0;
    dv.setUint32(o, 0); o += 4;
    dv.setBigUint64(o, BigInt(this.bundle.variantId)); o += 8;
    dv.setUint32(o, items.length); o += 4;
    for (const it of items) {
      dv.setBigUint64(o, BigInt(CAENE_RW_BUNDLE_COLLECTION_ID)); o += 8;
      dv.setBigUint64(o, BigInt(it.productId)); o += 8;
      dv.setBigUint64(o, BigInt(it.variantId)); o += 8;
      dv.setBigUint64(o, 1n); o += 8;
      dv.setUint32(o, 0); o += 4;
    }
    dv.setUint32(o, Math.floor(Date.now() / 1000)); o += 4;
    dv.setUint32(o, 0);
    return btoa(String.fromCharCode(...new Uint8Array(buf)));
  }

  async handleCtaClick() {
    if (this._isLoading) return;

    this._isLoading = true;
    this.errorEl.hidden = true;
    this.cta.disabled = true;
    this.cta.dataset.loading = 'true';
    this.ctaLabel.textContent = 'Lägger till…';

    try {
      // 1. Registrera bundle-selection hos Recharge → _rb_id
      const bundleRes = await fetch('/tools/recurring/bundling-storefront-manager/api/v1/bundles', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Recharge-App': 'storefront-client',
        },
        body: JSON.stringify({ bundle: this.buildBundleBlob() }),
      });
      const bundleJson = await bundleRes.json().catch(() => null);
      if (!bundleRes.ok || !bundleJson || !bundleJson.id) {
        throw new Error(`bundle-register failed (${bundleRes.status})`);
      }

      // 2. ATC bundle-varianten med selection-id
      const contents = this.state.map(s => `1x ${s.title.toLowerCase()}`).join('\n');
      const item = {
        quantity: 1,
        id: String(this.bundle.variantId),
        properties: { 'Contents': contents, '_rb_id': bundleJson.id },
      };
      // Trions namn → cart-raden visar "hot girl summer" i stället för bundle-produktnamnet
      if (this.bundle.trioName) item.properties['_trio_name'] = this.bundle.trioName;
      if (this.mode === 'sub' && this.subAvailable) {
        item.selling_plan = String(this.bundle.sellingPlanId);
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${res.status}`);
      }

      this.trackAdd();
      this.openCartDrawer();
    } catch (e) {
      console.error('[caene-rw] bundle cart-add failed', e);
      this.errorEl.hidden = false;
    } finally {
      this._isLoading = false;
      this.cta.disabled = false;
      this.cta.dataset.loading = 'false';
      this.ctaLabel.textContent = this.defaultCtaText;
    }
  }

  openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:added'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    // Open Rebuy SmartCart in-place. Polling pattern matchar theme.liquid
    // delegated cart-trigger handler (50×100ms = max 5s före /cart-fallback).
    // Tidigare fallback `window.location.href = '/cart'` triggade theme.liquid
    // Rescue 1 → redirect till /?caene_open_cart=1. Mikael 2026-05-15.
    let attempts = 0;
    const tryShow = () => {
      const R = window.Rebuy && window.Rebuy.SmartCart;
      if (R && typeof R.show === 'function') {
        R.show();
        // Poka rebuy-skin efter Rebuys re-render — annars missar trio-raden
        // pill/titel-dekoration (skinnets observer kan sitta på remountad nod).
        setTimeout(() => document.dispatchEvent(new CustomEvent('rebuy:cart.refresh')), 800);
        return;
      }
      if (++attempts >= 50) { window.location.href = '/cart'; return; }
      setTimeout(tryShow, 100);
    };
    tryShow();
  }

  handleVariantChange(e) {
    if (!e.detail || !e.detail.variant) return;
    const heroIdx = this.state.findIndex(s => s.isHero);
    if (heroIdx === -1) return;
    const v = e.detail.variant;
    if (v.id === this.state[heroIdx].variantId) return;

    this.state[heroIdx].variantId = v.id;
    this.state[heroIdx].fullPrice = v.price;

    this.renderCards();
    this.updateTotals();
  }

  trackAdd() {
    if (typeof window.dataLayer === 'undefined') return;
    const trio = this.products.find(p => p.isHero)?.handle || '';
    window.dataLayer.push({
      event: 'routine_widget_add',
      trio,
      mode: this.mode,
      bundle_variant_id: this.bundle.variantId,
      items: this.state.map(s => ({ handle: s.handle })),
      total_cents: this.bundleCurrent(),
    });
  }

  formatPrice(cents) {
    const kr = Math.round(cents / 100);
    return `${kr} kr`;
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('caene-routine-widget')) {
  customElements.define('caene-routine-widget', CaeneRoutineWidget);
}

(function trackViewObserver() {
  if (!('IntersectionObserver' in window) || typeof window.dataLayer === 'undefined') return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const widget = entry.target;
        try {
          const jsonScript = widget.querySelector('script[data-products-json]');
          const products = JSON.parse(jsonScript.textContent);
          const heroHandle = products.find(p => p.isHero)?.handle || '';
          window.dataLayer.push({
            event: 'routine_widget_view',
            trio: heroHandle,
            pdp_handle: heroHandle,
          });
        } catch (e) { /* noop */ }
        observer.unobserve(widget);
      }
    });
  }, { threshold: 0.3 });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('caene-routine-widget').forEach(w => observer.observe(w));
  });
})();
